import { useCallback, useContext, useEffect, useState } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { useAppContext } from "src/shared/context/AppContext";
import { CookiePreferencesContext } from "src/shared/context/cookie-preferences-context";
import { readEmailOptInSubscribed, writeEmailOptInSubscribed } from "../email-opt-in-subscription-storage";
import type { EmailOptInPopupVariant } from "./useEmailOptInPopupVariant";
const VISITED_KEY = "email_opt_in_visited";
const SESSION_DISMISSED_KEY = "email_opt_in_popup_dismissed_session";
const SESSION_PAGE_VIEWS_KEY = "email_opt_in_popup_page_views";

const readLocalStorageFlag = (key: string): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const writeLocalStorageFlag = (key: string) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    // localStorage unavailable — fall through silently
  }
};

const readSessionFlag = (key: string): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const writeSessionFlag = (key: string) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    // sessionStorage unavailable (private mode) — fall through silently
  }
};

const readSessionPageViews = (): number => {
  if (typeof window === "undefined") {
    return 0;
  }
  try {
    return Math.trunc(Number(window.sessionStorage.getItem(SESSION_PAGE_VIEWS_KEY) ?? "0")) || 0;
  } catch {
    return 0;
  }
};

const incrementSessionPageViews = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const current = readSessionPageViews();
    window.sessionStorage.setItem(SESSION_PAGE_VIEWS_KEY, String(current + 1));
  } catch {
    // sessionStorage unavailable (private mode) — fall through silently
  }
};

/**
 * Decides whether the non-blocking email opt-in popup should be visible right now.
 *
 * The popup is driven by `isTriggered` — the parent sets it when the email opt-in slot
 * scrolls into view (the arm-agnostic participation trigger for exp 103701). This hook adds
 * the remaining suppressors: the desktop viewport (the popup is desktop-only, and this guards
 * a resize after the trigger fires), cookie consent (never stack on the consent banner),
 * subscription state, per-session dismissal, logged-in users, and the UserEventToast that
 * shares the bottom-right corner.
 *
 * The variant gate (V1 2nd page view / V2 return visit) is resolved by the parent via
 * `isEligibleForPopupVariant`; it decides whether the slot renders the banner or the popup
 * trigger, not whether the popup is visible.
 */
export const useEmailOptInPopupVisibility = (isTriggered: boolean, hasUserEventToast: boolean) => {
  const appContext = useAppContext();
  const { hasSelectedPreferences } = useContext(CookiePreferencesContext);
  const isDesktop = useMediaQuery(Breakpoint.Desktop);

  // Long-lived flags persisted across sessions.
  const [isSubscribed, setIsSubscribed] = useState<boolean>(() => readEmailOptInSubscribed());
  const [hasVisitedBefore] = useState<boolean>(() => readLocalStorageFlag(VISITED_KEY));

  // Session-only flag — dismissing in this tab/session hides until next session.
  const [isDismissedThisSession, setIsDismissedThisSession] = useState<boolean>(() =>
    readSessionFlag(SESSION_DISMISSED_KEY),
  );

  // Mark this visit as "visited" once we know the user can participate. We persist on mount
  // (not on popup render) so users in Control still build up "visited" history — the flag
  // reflects browsing behavior, not popup exposure. The V2 gate reads this flag, so Control
  // must set it too or the split would be contaminated.
  useEffect(() => {
    if (appContext.isUserLoggedIn || hasVisitedBefore) {
      return;
    }
    writeLocalStorageFlag(VISITED_KEY);
    // Note: we intentionally do NOT setHasVisitedBefore(true) for this render. A user
    // landing on home for the very first time should not see the "second visit" variant
    // popup on this same page load — only on their next visit.
  }, [appContext.isUserLoggedIn, hasVisitedBefore]);

  // Increment the session page view counter on each mount — the V1 gate reads it.
  useEffect(() => {
    incrementSessionPageViews();
  }, []);

  const isVisible =
    isTriggered &&
    isDesktop &&
    !appContext.isUserLoggedIn &&
    !isSubscribed &&
    !isDismissedThisSession &&
    hasSelectedPreferences &&
    !hasUserEventToast;

  const dismiss = useCallback(() => {
    setIsDismissedThisSession(true);
    writeSessionFlag(SESSION_DISMISSED_KEY);
  }, []);

  const markSubscribed = useCallback(() => {
    setIsSubscribed(true);
    writeEmailOptInSubscribed();
  }, []);

  return { isVisible, dismiss, markSubscribed };
};

/**
 * Pure (non-hook) check for whether the popup arm is active for the given variant, ignoring
 * transient conditions like cookie-consent. When true the email opt-in slot swaps the banner
 * for the popup trigger, so the popup appears at the moment the banner would have.
 *
 * Variant 1 → needs 2+ session page views.
 * Variant 2 → needs a prior visit (localStorage flag).
 * Control (0) → banner always.
 */
export const isEligibleForPopupVariant = (variant: EmailOptInPopupVariant): boolean => {
  if (variant === 0) return false;
  // +1 counts the current page view — the stored counter increments in an effect that runs after this render.
  if (variant === 1) return readSessionPageViews() + 1 >= 2;
  if (variant === 2) return readLocalStorageFlag(VISITED_KEY);
  return false;
};
